import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import { Loader2, RefreshCw, Search, Undo2 } from "lucide-react";
import RefundOrderModal from "../components/retail/admin/RefundOrderModal";
import OrderDetailsDrawer from "../components/retail/admin/OrderDetailsDrawer";
import { useCashTender } from "../components/CashTenderWatcher";
import { externalPaidCents } from "../lib/orderPaymentBreakdown";
import { paymentStatusColor, paymentStatusLabel } from "../lib/paymentStatusDisplay";
import { modePresentation, promiseLine, isScheduledOrder } from "../lib/retail/fulfillmentPresentation";

// Phase 3D — Tenant-facing Retail Orders page. Auth-required dashboard view.
// Lightweight on purpose: a single table of orders with inline status updates.
// A full kitchen dashboard is out of scope for Phase 3D.

const STATUS_OPTIONS = ["placed", "accepted", "ready", "completed", "cancelled"];
// Sprint A (Feb 2026) — human labels for the fulfillment status
// dropdown. "cancelled" reads better as "Order Cancelled" to
// distinguish from item-level cancellations (Sprint B).
// Kitchen/Delivery/Payment status-axis separation correction (Aug
// 2026) — this column now reads explicitly as "Kitchen Status" (see
// the table header below); the LABEL/COLOR maps and stored values are
// unchanged so existing filters/PATCH calls keep working exactly as
// before.
const STATUS_LABEL = {
  placed: "Placed",
  accepted: "Accepted",
  ready: "Ready",
  completed: "Completed",
  cancelled: "Order Cancelled",
};
const STATUS_COLOR = {
  placed: "bg-slate-100 text-slate-700",
  accepted: "bg-blue-100 text-blue-700",
  ready: "bg-amber-100 text-amber-800",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

// Kitchen/Delivery/Payment status-axis separation correction (Aug
// 2026) — a genuinely independent axis (`retail_orders.delivery_status`,
// mirrored from `LocalDelivery.status` by `event_application.py` — see
// PR #113) shown ONLY for Local Delivery orders, never conflated with
// the kitchen `status` column above. Labels mirror
// `OrderLocalDeliveryPanel.jsx`'s own tenant-facing vocabulary, kept as
// a separate copy. (The payment vocabulary this comment used to point
// at as precedent is no longer local: three private copies of it had
// drifted, so it moved to `lib/paymentStatusDisplay.js` — see the
// Sep 2026 note below.)
const DELIVERY_STATUS_LABEL = {
  not_applicable: "Not applicable",
  quote_pending: "Getting delivery quote…",
  quoted: "Quote received",
  quote_expired: "Quote expired",
  dispatch_pending: "Requesting courier…",
  courier_requested: "Courier requested",
  courier_assigned: "Courier assigned",
  en_route_to_pickup: "Courier en route to pickup",
  at_pickup: "Courier at restaurant",
  picked_up: "Picked up by courier",
  en_route_to_dropoff: "Out for delivery",
  delivered: "Delivered",
  cancellation_pending: "Cancellation pending",
  canceled: "Delivery canceled",
  delivery_failed: "Delivery request failed",
  returned: "Returned",
  reconciliation_required: "Needs attention",
};
const DELIVERY_STATUS_COLOR = {
  delivered: "bg-emerald-100 text-emerald-700",
  courier_requested: "bg-sky-100 text-sky-700",
  courier_assigned: "bg-sky-100 text-sky-700",
  en_route_to_pickup: "bg-sky-100 text-sky-700",
  at_pickup: "bg-sky-100 text-sky-700",
  picked_up: "bg-sky-100 text-sky-700",
  en_route_to_dropoff: "bg-sky-100 text-sky-700",
  dispatch_pending: "bg-slate-100 text-slate-600",
  delivery_failed: "bg-rose-100 text-rose-700",
  canceled: "bg-rose-100 text-rose-700",
  reconciliation_required: "bg-amber-100 text-amber-800",
  quote_expired: "bg-amber-100 text-amber-800",
};

// Own Delivery/KDS status-wording correction (Aug 2026) — an Own
// Delivery order's `delivery_status` above is the shared, Uber-
// vocabulary `DeliveryStatus` projection (e.g. "dispatch_pending" ->
// "Requesting courier…"), which is actively FALSE for a provider that
// never requests an Uber courier at all. `own_delivery_phase` is the
// safe, provider-owned source of truth for these orders — this label
// map mirrors `OwnDeliveryOperationalPanel.jsx`'s own `PHASE_LABEL`
// (kept as a separate copy, matching this file's own existing
// convention for the Uber-facing labels above).
const OWN_DELIVERY_PHASE_LABEL = {
  accepted: "Accepted",
  preparing: "Preparing",
  ready_for_delivery: "Ready for Delivery",
  driver_assigned: "Driver Assigned",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  delivery_failed: "Delivery Failed",
  delivery_cancelled: "Delivery Cancelled",
};
const OWN_DELIVERY_PHASE_COLOR = {
  accepted: "bg-slate-100 text-slate-600",
  preparing: "bg-sky-100 text-sky-700",
  ready_for_delivery: "bg-sky-100 text-sky-700",
  driver_assigned: "bg-indigo-100 text-indigo-700",
  out_for_delivery: "bg-indigo-100 text-indigo-700",
  delivered: "bg-emerald-100 text-emerald-700",
  delivery_failed: "bg-rose-100 text-rose-700",
  delivery_cancelled: "bg-rose-100 text-rose-700",
};

// Feb 2026 V1 launch (Sprint 1) — dedicated payment-state visibility so
// operators can tell payment status apart from kitchen state.
// Sprint A extends this with prominent bold pills for the two refund
// states so a fully-refunded order can't be missed at a glance.
// Sep 2026 (independent review of PR #185) — the label, colour and
// captured-status vocabularies moved to `lib/paymentStatusDisplay.js`,
// shared with `OrderDetailsDrawer.jsx`. Three private copies had drifted
// apart, and the drift was visible to the tenant: PR #185 began writing
// a new `cancelled` order status that neither map knew, so an abandoned
// kiosk attempt still read "Pending" here — the exact symptom that PR
// set out to remove.


// Sprint A — Payment Mode column. Cash & Card are the customer-flow
// values; Credit & Check are operator-only classifications set via the
// details drawer (no A/R automation yet).
const PAYMENT_METHOD_LABEL = {
  cash: "Cash",
  card: "Card",
  credit: "Credit",
  check: "Check",
  card_pending: "Card",
  pay_later: "Pay Later",
};

function formatMoney(v, currency) {
  if (v == null) return "";
  const sym = { USD: "$", INR: "₹", EUR: "€", GBP: "£" }[(currency || "").toUpperCase()] || "";
  return sym ? `${sym}${Number(v).toFixed(2)}` : `${Number(v).toFixed(2)} ${currency || ""}`.trim();
}

function formatMoneyCents(cents, currency) {
  if (cents == null || !Number.isFinite(Number(cents))) return "";
  return formatMoney(Number(cents) / 100, currency);
}

// Cash-confirmation safety correction (Sep 2026) — THE authoritative
// figure staff are asked to collect, derived from the full order
// document exactly the way the backend derives it.
//
// This is a deliberate mirror of `services/loyalty_rules.py`'s
// `external_payable_cents`: total, less the loyalty tender that has
// already paid part of this order. It is NOT a new frontend pricing
// calculation — `order.total` stays the full price of the order (what
// receipts and the list's Total column must keep showing) and this is
// the subset of it a member of staff at the till should ever be handed.
//
// An order such as RUD-ORD-000044 — $2.17 total, $1.00 settled in
// points — is collectible at $1.17. Showing the gross $2.17 on a
// confirmation dialog would ask staff to over-collect by a dollar.
//
// Returns `null` — never a guess, and never a fallback to the gross
// total — whenever the amount cannot be established from the document.
// Every caller treats `null` as fail-closed.
//
// Sep 2026 — the arithmetic now lives in `lib/orderPaymentBreakdown.js`
// so the confirmation dialog, the receipts and the order-details payment
// summary all derive the collected amount from ONE implementation. This
// export is kept because it is the name this page's callers and tests
// already use.
export function collectibleCashCents(orderDoc) {
  return externalPaidCents(orderDoc);
}

// Single-owner cash dialog (Sep 2026) — why this page is split in two.
// ------------------------------------------------------------------------
// The one `CashTenderDialog` on the dashboard is owned by
// `CashTenderWatcher`, which provides its context INSIDE `DashboardLayout`.
// A single component that both renders `DashboardLayout` and calls
// `useCashTender()` puts the provider BELOW the consumer, so the hook
// returns null on every render and the recovery button falls straight
// into its error branch.
//
// Splitting fixes that structurally rather than papering over the
// symptom: the shell renders the layout, and the content component runs
// inside it, where the controller actually lives.
export default function RetailOrdersAdminPage() {
  return (
    <DashboardLayout>
      <RetailOrdersAdminContent />
    </DashboardLayout>
  );
}

function RetailOrdersAdminContent() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [search, setSearch] = useState("");
  // Phase 3L.3 — tablet live-order polling. Track the highest order id
  // we've seen so newly-arrived rows can be flagged with a NEW badge
  // until the operator interacts with them.
  const [seenIds, setSeenIds] = useState(() => new Set());
  const [livePolling, setLivePolling] = useState(true);
  // Commerce Cleanup + KDS Sprint (Feb 2026) — owner refund modal target.
  const [refundOrder, setRefundOrder] = useState(null);
  // Sprint A (Feb 2026) — clickable order code opens a right-side
  // details drawer with the full order breakdown, refund history,
  // status history, and operator record-correction control.
  const [detailsOrder, setDetailsOrder] = useState(null);
  // Resolve the tenant's chatbot slug once so the "Open Kitchen Display"
  // button can deep-link to /kitchen/<slug> without making the operator
  // type their own slug on the tablet.
  const [kdsSlug, setKdsSlug] = useState("");
  useEffect(() => {
    let alive = true;
    api.get("/company")
      .then((r) => { if (alive) setKdsSlug((r.data?.chatbot?.slug || "")); })
      .catch(() => { /* best effort — the button degrades to a hint */ });
    return () => { alive = false; };
  }, []);

  const load = (filter = statusFilter, opts = {}) => {
    const silent = Boolean(opts.silent);
    if (!silent) setLoading(true);
    setError(null);
    const params = filter ? { status: filter } : {};
    api.get("/retail/orders", { params })
      .then((r) => {
        const fresh = r.data.orders || [];
        // On the first load, treat every existing row as already seen.
        // On subsequent polls, anything not in seenIds is genuinely NEW.
        if (seenIds.size === 0 && !silent) {
          setSeenIds(new Set(fresh.map((o) => o.id)));
        }
        setOrders(fresh);
        setTotal(r.data.total || 0);
      })
      .catch((e) => {
        const status = e?.response?.status;
        if (status === 403) {
          setError("Retail mode isn't enabled for this account.");
        } else {
          setError("Could not load retail orders.");
        }
      })
      .finally(() => { if (!silent) setLoading(false); });
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 3L.3 — 5-second polling cycle while the tablet view is active.
  // Silent reloads only — no spinner flicker.
  useEffect(() => {
    if (!livePolling) return;
    const id = setInterval(() => load(statusFilter, { silent: true }), 5000);
    return () => clearInterval(id);
  }, [livePolling, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark a row as seen (called when the operator touches the status select).
  const markSeen = (orderId) => {
    if (seenIds.has(orderId)) return;
    setSeenIds((prev) => { const n = new Set(prev); n.add(orderId); return n; });
  };

  const updateStatus = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    const target = orders.find((o) => o.id === orderId);
    try {
      const { data } = await api.patch(`/retail/orders/${orderId}/status`, { status: newStatus });
      // Re-fetch so totals & ordering stay accurate.
      await load();
      toast.success(`Order ${target?.order_code || ""} → ${newStatus}`.trim());
      // Tenant-owned production dispatch (Aug 2026) — this used to be
      // an internal-ops-only alert; the tenant who just clicked cancel
      // is the one who needs to know a courier may still be en route.
      if (data?.local_delivery_cancellation_notice) {
        toast.warning(data.local_delivery_cancellation_notice, { duration: 15000 });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "object" && detail?.message) ? detail.message
                  : (typeof detail === "string" ? detail : "Could not update status");
      toast.error(msg);
    } finally {
      setUpdatingId(null);
    }
  };

  // Go-Live Correction (Feb 2026) — Cash Payment Confirmation Workflow.
  // Dedicated staff gesture that ONLY flips payment_status → paid on a
  // dine-in cash "Pay at Counter" order. Order status stays at
  // "placed"; the operator advances it to "accepted" via the normal
  // dropdown afterwards. This split prevents kitchen preparation
  // before cash is physically collected and makes the audit trail
  // legible (separate payment vs. status transitions).
  //
  // Cash-confirmation safety correction (Sep 2026) + automatic popup
  // (restored Sep 2026)
  // ----------------------------------------------------------------
  // The first click used to BE the financial mutation: `onClick` went
  // straight to `PATCH .../mark-cash-received`, which stamps
  // `cash_received_by`, `cash_received_at` and
  // `payment_confirmed_source: "cash_staff_confirmation"` on the order.
  // One mis-tap on a tablet, in a list that re-sorts under the finger
  // every five seconds, irreversibly recorded money as collected — and
  // the operator was never shown how much money that was.
  //
  // This page no longer owns that gesture, and no longer mounts a cash
  // dialog of its own. `CashTenderWatcher` is the single controller for
  // the dashboard: it polls the server's pending-cash queue, raises the
  // dialog automatically when a cash order is waiting, and owns the one
  // confirmation path. Two `CashTenderDialog`s could otherwise be on
  // screen at once — this one and the automatic popup — each with its
  // own key handling and its own idea of what was outstanding.
  //
  // "Collect Cash" below is the MANUAL ENTRY / RECOVERY route into that
  // same dialog: for a popup that was dismissed or missed, for a shift
  // where several cash orders are waiting and the cashier wants a
  // particular one, or for any moment the automatic presentation was
  // unavailable. It asks the controller to bring that order to the front
  // of the SHARED queue. It marks nothing paid.
  //
  // The controller re-reads the server's queue (paging to the end, so an
  // order past the first page is still reachable) and takes the
  // authoritative amount due and quick amounts from it, never from the
  // row rendered in this list — a stale page must not be able to propose
  // a stale total. It echoes that figure back as `expected_amount_cents`,
  // which the server refuses on mismatch.
  const cashTender = useCashTender();

  // The confirmation happens in the controller now, so this list is TOLD
  // when one lands rather than doing the PATCH and reloading itself.
  // Without this the row that launched the dialog would keep offering
  // "Collect Cash" until the operator reloaded the page.
  useEffect(() => {
    if (!cashTender?.onCollected) return undefined;
    return cashTender.onCollected(() => load(statusFilter, { silent: true }));
  }, [cashTender, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual entry into the SAME dialog. Makes ZERO mutation requests.
  const openCashTender = async (orderId) => {
    if (!orderId) return;
    setUpdatingId(orderId);
    try {
      if (!cashTender) {
        toast.error("Could not open the cash payment dialog");
        return;
      }
      const opened = await cashTender.requestCashTender(orderId);
      if (!opened) {
        // The server no longer lists it as collectable — say so rather
        // than opening a dialog against an order that cannot be paid.
        toast.error("This order is no longer awaiting cash payment.");
        await load();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        (detail && typeof detail === "object" && detail.message) ? detail.message
          : (typeof detail === "string" ? detail : "Could not open the cash payment dialog"),
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const visibleOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) =>
      (o.order_code || "").toLowerCase().includes(q) ||
      (o.customer?.name || "").toLowerCase().includes(q) ||
      (o.customer?.phone || "").toLowerCase().includes(q) ||
      (o.customer?.email || "").toLowerCase().includes(q)
    );
  }, [orders, search]);

  return (
    <>
      <div className="space-y-6" data-testid="retail-orders-admin">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Retail Orders</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Orders placed through your public retail menu. Update status as they move through the kitchen.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={kdsSlug ? `/kitchen/${kdsSlug}` : "#"}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (!kdsSlug) {
                  e.preventDefault();
                  toast.info("Your tenant slug is still loading — please retry in a moment.");
                }
              }}
              data-testid="retail-orders-open-kds"
              className="text-xs px-3 py-1.5 rounded-lg border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 inline-flex items-center gap-1.5"
            >
              Open Kitchen Display
            </a>
            <label className="text-xs inline-flex items-center gap-1.5 text-slate-600" data-testid="retail-orders-live-toggle">
              <input
                type="checkbox"
                checked={livePolling}
                onChange={(e) => setLivePolling(e.target.checked)}
              />
              <span className="inline-flex items-center gap-1">
                <span className={"w-1.5 h-1.5 rounded-full " + (livePolling ? "bg-emerald-500 animate-pulse" : "bg-slate-300")} />
                Live (5s)
              </span>
            </label>
            <button
              data-testid="retail-orders-refresh"
              onClick={() => load()}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border hover:bg-slate-50"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        </header>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Filter:</span>
          {["", ...STATUS_OPTIONS].map((s) => (
            <button
              key={s || "all"}
              data-testid={`retail-orders-filter-${s || "all"}`}
              onClick={() => setStatusFilter(s)}
              className={
                "text-xs px-3 py-1 rounded-full border transition " +
                (statusFilter === s
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white border-slate-200 text-slate-600 hover:border-slate-300")
              }
            >
              {s ? s : "All"}
            </button>
          ))}
          <span className="ml-auto text-xs text-muted-foreground">
            Showing {visibleOrders.length} of {total}
          </span>
        </div>

        {/* Always-on search bar — surfaces the feature even before the first order
            is placed. Disabled state is implicit (empty input is no-op). */}
        <label className="relative block max-w-md" data-testid="retail-orders-search-wrapper">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" aria-hidden="true" />
          <input
            data-testid="retail-orders-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by order code, name, phone, email…"
            disabled={orders.length === 0}
            className="w-full h-9 rounded-md border border-slate-200 pl-8 pr-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900/20 disabled:bg-slate-50 disabled:text-slate-400"
          />
        </label>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="retail-orders-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-12 grid place-items-center">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 bg-white border border-dashed border-slate-200 rounded-xl">
            <div className="text-sm text-slate-500">No orders yet.</div>
          </div>
        ) : visibleOrders.length === 0 && search.trim() !== "" ? (
          <div className="text-center py-12 bg-white border border-dashed border-slate-200 rounded-xl" data-testid="retail-orders-search-empty">
            <div className="text-sm text-slate-500">
              No matches for “{search}”.{" "}
              <button
                type="button"
                onClick={() => setSearch("")}
                className="underline-offset-2 hover:underline"
                data-testid="retail-orders-search-empty-clear"
              >
                Clear search
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50">
                <tr>
                  <th className="px-3 py-2">Order</th>
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">Mode</th>
                  <th className="px-3 py-2">Items</th>
                  <th className="px-3 py-2">Total</th>
                  <th className="px-3 py-2">Payment Mode</th>
                  <th className="px-3 py-2">Payment</th>
                  <th className="px-3 py-2">Placed</th>
                  <th className="px-3 py-2">Kitchen Status</th>
                  <th className="px-3 py-2">Delivery Status</th>
                  <th className="px-3 py-2 text-right">Refund</th>
                </tr>
              </thead>
              <tbody>
                {visibleOrders.map((o) => {
                  const isNew = seenIds.size > 0 && !seenIds.has(o.id);
                  // Commerce Cleanup + KDS Sprint (Feb 2026) — refund is
                  // only meaningful once payment is captured. Cash + card
                  // both qualify; the modal handles the method-specific
                  // copy.
                  const ps = (o.payment_status || "").toLowerCase();
                  const canRefund = ["paid", "partial_refund"].includes(ps) && !!o.payment_id;
                  // Sprint A (Feb 2026) — fully refunded orders are
                  // frozen: no further status changes, no further refunds.
                  const isFullyRefunded = ps === "refunded";
                  const pmKey = (o.payment_method || "").toLowerCase();
                  // Sprint 1 Go-Live (Feb 2026, item #8) — Dine-in cash
                  // "Pay at Counter": the row is waiting on staff to
                  // collect cash before entering the kitchen queue. In
                  // this state the operator sees a prominent
                  // "Mark Cash Received & Accept" button that flips
                  // payment_status=paid + status=accepted in one action.
                  const awaitingCash = (
                    o.awaiting_staff_payment === true
                    && pmKey === "cash"
                    && ps === "pending"
                    && o.status === "placed"
                  );
                  return (
                  <tr
                    key={o.id}
                    // Sep 2026 — a subtle per-mode tint plus a left rule, so
                    // an operator can pick Pickup out of a Dine-In list at a
                    // glance. Colour is never the only signal: the Mode cell
                    // still carries an icon and the text label. The NEW pulse
                    // deliberately still wins, because "just arrived" is more
                    // urgent than "which mode".
                    className={
                      "border-t border-slate-100 border-l-4 "
                      + (isNew
                        ? "bg-amber-50/60 animate-pulse-once border-l-amber-400"
                        : `${modePresentation(o.fulfillment_mode).tint} ${modePresentation(o.fulfillment_mode).border}`)
                    }
                    data-testid={`retail-order-row-${o.id}`}
                    data-mode={o.fulfillment_mode || ""}
                  >
                    <td className="px-3 py-2 font-mono">
                      <div className="inline-flex items-center gap-2">
                        {isNew && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white" data-testid={`retail-order-new-${o.id}`}>NEW</span>
                        )}
                        {/* Sprint A — clickable order code opens the
                            details drawer. Uses a button so it doesn't
                            navigate away from the live-polling list. */}
                        <button
                          type="button"
                          onClick={() => { markSeen(o.id); setDetailsOrder(o); }}
                          data-testid={`retail-order-code-link-${o.id}`}
                          className="text-orange-600 hover:text-orange-700 hover:underline font-semibold"
                          title="View order details"
                        >
                          {o.order_code}
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {o.customer_id ? (
                        <Link
                          to={`/dashboard/customers/${o.customer_id}`}
                          className="font-medium text-accent hover:underline"
                          data-testid={`retail-order-customer-link-${o.id}`}
                          title="Open customer profile"
                        >
                          {o.customer?.name || "—"}
                        </Link>
                      ) : (
                        <div className="font-medium">{o.customer?.name || "—"}</div>
                      )}
                      <div className="text-xs text-slate-500">{o.customer?.phone || ""}</div>
                    </td>
                    <td className="px-3 py-2 text-xs" data-testid={`retail-order-fulfillment-mode-${o.id}`}>
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${modePresentation(o.fulfillment_mode).chip}`}
                        data-testid={`retail-order-mode-chip-${o.id}`}
                      >
                        <span aria-hidden="true">{modePresentation(o.fulfillment_mode).icon}</span>
                        {o.fulfillment_mode ? modePresentation(o.fulfillment_mode).label : "—"}
                      </span>
                      {/* Sep 2026 — "Pickup" alone does not tell an operator
                          WHEN. For a scheduled order the promised window is
                          the operationally important fact, so it sits right
                          under the mode. ASAP rows render nothing extra. */}
                      {isScheduledOrder(o) && (
                        <div
                          className="mt-0.5 text-[11px] font-medium text-sky-800"
                          data-testid={`retail-order-scheduled-promise-${o.id}`}
                        >
                          {promiseLine(o)}
                        </div>
                      )}
                      {o.fulfillment_mode === "delivery" && (
                        <span
                          data-testid={`retail-order-local-delivery-badge-${o.id}`}
                          className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-orange-100 text-orange-700 align-middle"
                        >
                          LOCAL
                        </span>
                      )}
                      {o.fulfillment_mode === "shipping" && (
                        <span
                          data-testid={`retail-order-shipping-badge-${o.id}`}
                          className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 align-middle"
                          title="Parcel shipping order"
                        >
                          PARCEL
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">{o.item_count}</td>
                    <td className="px-3 py-2">{formatMoney(o.total, o.currency)}</td>
                    <td className="px-3 py-2 text-xs" data-testid={`retail-order-payment-method-${o.id}`}>
                      {PAYMENT_METHOD_LABEL[pmKey] || (pmKey ? pmKey : "—")}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        data-testid={`retail-order-payment-status-${o.id}`}
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${paymentStatusColor(o.payment_status)}`}
                      >
                        {paymentStatusLabel((o.payment_status || "pending").toLowerCase(), o.payment_release_reason)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {o.placed_at ? new Date(o.placed_at).toLocaleString() : ""}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLOR[o.status] || STATUS_COLOR.placed}`}>
                          {STATUS_LABEL[o.status] || o.status}
                        </span>
                        {awaitingCash ? (
                          <button
                            type="button"
                            data-testid={`retail-order-mark-cash-received-${o.id}`}
                            disabled={updatingId === o.id}
                            onClick={() => { markSeen(o.id); openCashTender(o.id); }}
                            title="Open the cash payment dialog: check the amount due, record what the customer handed over, read the change, then confirm. The order will remain in Placed until you advance it."
                            className="text-xs h-7 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {/* Terminology (Sep 2026). This action
                                STARTS collection, so it reads as an
                                instruction — "Collect Cash". "Cash
                                Received" is reserved for the amount the
                                customer handed over (inside the dialog)
                                and for the final confirmation, which is
                                the only place that wording is a
                                statement of fact. Clicking this marks
                                nothing paid. Once collection succeeds
                                the order is Paid, `awaitingCash` goes
                                false, and this action disappears. */}
                            {updatingId === o.id ? "Opening…" : "Collect Cash"}
                          </button>
                        ) : (
                          <select
                            data-testid={`retail-order-status-${o.id}`}
                            value={o.status}
                            disabled={updatingId === o.id || isFullyRefunded}
                            title={isFullyRefunded ? "Fully refunded orders are locked" : undefined}
                            onChange={(e) => { markSeen(o.id); updateStatus(o.id, e.target.value); }}
                            className="text-xs h-7 rounded border border-slate-200 px-1 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {STATUS_OPTIONS
                              // Sep 2026 — a Ready order is not offered
                              // "Cancelled". The food has been made, so
                              // the supported path is Complete then
                              // Refund. This is the UI half of the rule
                              // only: `PATCH /retail/orders/{id}/status`
                              // rejects the hop with 409
                              // `cannot_cancel_ready_order`, and
                              // `orders_state.ALLOWED_TRANSITIONS` no
                              // longer contains it at all, so a stale
                              // page cannot get around it.
                              .filter((s) => !(s === "cancelled" && o.status === "ready"))
                              .map((s) => (
                              <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {/* Kitchen/Delivery/Payment status-axis separation
                          correction (Aug 2026) — a completed kitchen
                          status must never hide a delivery failure, and
                          a failed delivery must never be conflated with
                          the restaurant's own status. Non-delivery
                          orders show a plain dash — never invent a
                          delivery state for an order that never had one. */}
                      {o.fulfillment_mode === "delivery" ? (
                        // Own Delivery/KDS status-wording correction (Aug
                        // 2026) — an Own Delivery order (`fulfillment_choice
                        // === "own"`) must NEVER render the shared, Uber-
                        // vocabulary `delivery_status` wording; it renders
                        // its own `own_delivery_phase` label instead, even
                        // when that phase hasn't been recorded yet (falls
                        // back to a dash, never a fabricated Uber status).
                        o.fulfillment_choice === "own" ? (
                          o.own_delivery_phase ? (
                            <span
                              data-testid={`retail-order-delivery-status-${o.id}`}
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${OWN_DELIVERY_PHASE_COLOR[o.own_delivery_phase] || "bg-slate-100 text-slate-600"}`}
                            >
                              {OWN_DELIVERY_PHASE_LABEL[o.own_delivery_phase] || o.own_delivery_phase}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400" data-testid={`retail-order-delivery-status-${o.id}`}>—</span>
                          )
                        ) : o.delivery_status ? (
                          <span
                            data-testid={`retail-order-delivery-status-${o.id}`}
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${DELIVERY_STATUS_COLOR[o.delivery_status] || "bg-slate-100 text-slate-600"}`}
                          >
                            {DELIVERY_STATUS_LABEL[o.delivery_status] || o.delivery_status}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400" data-testid={`retail-order-delivery-status-${o.id}`}>—</span>
                        )
                      ) : (
                        <span className="text-xs text-slate-300" data-testid={`retail-order-delivery-status-${o.id}`}>Not applicable</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        disabled={!canRefund || isFullyRefunded}
                        onClick={() => setRefundOrder(o)}
                        data-testid={`retail-order-refund-${o.id}`}
                        title={isFullyRefunded ? "This order is fully refunded" : (canRefund ? "Issue refund" : "No captured payment to refund")}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-200 hover:border-orange-400 hover:bg-orange-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:bg-transparent"
                      >
                        <Undo2 className="w-3 h-3" /> Refund
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {refundOrder && (
        <RefundOrderModal
          order={refundOrder}
          onClose={() => setRefundOrder(null)}
          onRefunded={() => load(statusFilter, { silent: true })}
        />
      )}
      {detailsOrder && (
        <OrderDetailsDrawer
          order={detailsOrder}
          onClose={() => setDetailsOrder(null)}
          onChanged={() => load(statusFilter, { silent: true })}
          onRequestRefund={() => { setRefundOrder(detailsOrder); setDetailsOrder(null); }}
        />
      )}
    </>
  );
}
